import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import config from '../config';

const AuthContext = createContext();

export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
    const navigate = useNavigate();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [accessToken, setAccessToken] = useState(localStorage.getItem('access_token') || null);
    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null); // New state to store the user's profile
    const [loading, setLoading] = useState(true);

    const fetchUserDetails = async (token) => {
        try {
            const response = await api.get('/users/get-user-details/');

            // console.log("Fetched user data:", response.data);
            
            // Debug the user object structure
            const userData = {
                id: response.data.user_id || response.data.id,
                first_name: response.data.first_name,
                last_name: response.data.last_name,
                email: response.data.email,
                username: response.data.username,
                phone: response.data.phone,
                role: response.data.role
            };
            // console.log("🔍 USER DATA STRUCTURE:", userData);
            
            setUser({
                id: response.data.user_id || response.data.id,  // Handle different naming
                first_name: response.data.first_name,
                last_name: response.data.last_name,
                email: response.data.email,
                username: response.data.username,
                phone: response.data.phone,
                role: response.data.role,
                subscription_start_date: response.data.subscription_start_date,
                subscription_end_date: response.data.subscription_end_date,
                subscription_status: response.data.subscription_status,
                days_remaining: response.data.days_remaining,
            });

            // Fetch user profile using username
            const profileResponse = await api.get(`/profiles/profile/username/${response.data.username}/`);
            setUserProfile(profileResponse.data);
            // console.log("User Profile:", profileResponse.data);
            setIsLoggedIn(true);
        } catch (err) {
            // console.error('Error fetching user details:', err);
            setIsLoggedIn(false);
            setUser(null);
            setUserProfile(null); // Clear profile data in case of error
            logout();
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (token) {
            fetchUserDetails(token);
        } else {
            setLoading(false);
        }
    }, []);

    const login = async (email, password) => {
        try {
            // console.log('Attempting login with email:', email);

            // Use the configured api instance with _noAuth flag to skip auth header
            const response = await api.post(
                '/users/login/',
                { email, password },
                { _noAuth: true } // This tells our interceptor not to add auth header
            );

            // console.log('Login response status:', response.status);
            // console.log('Login response data:', response.data);

            const data = response.data;

            if (response.status !== 200) {
                throw new Error(data.detail || data.error || 'Login failed. Please check your credentials and try again.');
            }

            if (data.access && data.refresh) {
                const { access, refresh, user: userData } = data;

                // Store tokens
                localStorage.setItem('access_token', access);
                localStorage.setItem('refresh_token', refresh);

                // Update state
                setAccessToken(access);
                setIsLoggedIn(true);

                // Set user data from response
                if (userData) {
                    setUser({
                        id: userData.id,
                        email: userData.email,
                        first_name: userData.first_name,
                        last_name: userData.last_name,
                        username: userData.username,
                        role: userData.role,
                        subscription_status: userData.subscription_status,
                        days_remaining: userData.days_remaining,
                        is_active: userData.is_active,
                        is_staff: userData.is_staff,
                        is_superuser: userData.is_superuser,
                    });
                }

                // Fetch additional user details
                await fetchUserDetails(access);

                // Navigate to dashboard
                navigate('/dashboard');
                return access;
            } else {
                throw new Error('Invalid response from server');
            }
        } catch (error) {
            // console.error('Login failed:', error);
            // Clear any invalid tokens
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            setAccessToken(null);
            setIsLoggedIn(false);
            setUser(null);
            setUserProfile(null);

            // Provide more specific error message
            const errorMessage = error.response?.data?.error ||
                error.response?.data?.detail ||
                'Login failed. Please check your credentials and try again.';
            throw new Error(errorMessage);
        }
    };

    const autoLogin = async (token) => {
        try {
            localStorage.setItem('access_token', token);
            localStorage.setItem('refresh_token', localStorage.getItem('refresh_token')); // Keep existing refresh token
            setAccessToken(token);
            setIsLoggedIn(true);
            await fetchUserDetails(token);
            return true;
        } catch (error) {
            // console.error('Auto login failed:', error);
            return false;
        }
    };

    const logout = () => {
        setIsLoggedIn(false);
        setAccessToken(null);
        setUser(null);
        setUserProfile(null); // Clear profile data on logout
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        navigate('/');
    };

    return (
        <AuthContext.Provider value={{ isLoggedIn, user, loading, login, logout, autoLogin, accessToken, userProfile }}>
            {children}
        </AuthContext.Provider>
    );
};
